<script setup>
  import {computed, inject, reactive, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentFakeInput from "@components/Inputs/FakeInput.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {useForm, usePage} from "@inertiajs/vue3";
  import {laborbardeviationValues, patientGfralgorithm} from "@pages/Records/Components/Sections/LaborBar/enums.js";
  import {
    getDeviationFromValue,
    getReferenceText,
    getReferenceValue,
  } from "@pages/Records/Components/Sections/LaborBar/Utils/laborvalue.js";
  import {MedButton, MedInfoButton, MedLink} from "@pharma4u/medicheck-components";
  import Gfr from "@utils/Gfr.js";
  import NumberHelper from "@utils/Helpers/NumberHelper.js";
  import {cloneDeep} from "es-toolkit";

  defineOptions({
    name: "LaborTableGfrCalculator",
  });

  const page = usePage();

  const privacy = inject("$privacy");
  const setIsMassUpdateProcessing = inject("setIsMassUpdateProcessing");

  const refGfrDialog = ref(null);

  const isLoading = ref(false);
  const state = reactive({
    gender: null,
    gfrWeightOrBmiError: false,
  });

  const laborForm = useForm({
    weight: {},
    bmi: {},
    creatinine: {},
    gfr: {
      gfr_algorithm: "",
    },
    size: {},
  });

  const bmiReferenceValue = computed(() => getReferenceValue(cloneDeep(laborForm.bmi), state.gender));
  const creatinineReferenceValue = computed(() => getReferenceValue(cloneDeep(laborForm.creatinine), state.gender));
  const gfrReferenceValue = computed(() => getReferenceValue(cloneDeep(laborForm.gfr), state.gender));

  const bmiHelperText = computed(() => {
    if (laborForm.bmi.value <= 0) {
      return null;
    }

    let deviation = "";

    if (laborForm.bmi.deviation === "lowered") {
      deviation = " (↓)";
    } else if (laborForm.bmi.deviation === "raised") {
      deviation = " (↑)";
    }

    return "BMI: " + laborForm.bmi.value + " kg/m²" + deviation;
  });

  const creatinineReferenceText = computed(() => {
    return creatinineReferenceValue.value && laborForm.creatinine.value
      ? getReferenceText(creatinineReferenceValue.value)
      : null;
  });

  const gfrReferenceText = computed(() => {
    return gfrReferenceValue.value && laborForm.gfr.value ? getReferenceText(gfrReferenceValue.value) : null;
  });

  const gfrValue = computed(() => {
    return !laborForm.errors.gfr && laborForm.gfr.gfr_algorithm?.length > 0 ? laborForm.gfr.value : "–";
  });

  const hasError = computed(
    () =>
      !!(
        state.gfrWeightOrBmiError ||
        laborForm.errors.bmi ||
        laborForm.errors.gfr ||
        laborForm.errors.size > 0 ||
        laborForm.errors.weight ||
        laborForm.errors.creatinine
      ),
  );

  const open = (laborValues) => {
    laborForm.weight = cloneDeep(laborValues.weight) ?? {};
    laborForm.bmi = cloneDeep(laborValues.bmi) ?? {};
    laborForm.creatinine = cloneDeep(laborValues.creatinine) ?? {};
    laborForm.gfr = cloneDeep(laborValues.gfr) ?? {};
    laborForm.size = cloneDeep(laborValues.size) ?? {};

    laborForm.gfr.gfr_algorithm = page.props.record.gfralgorithm ?? null;

    state.gender = page.props.patient.gender;

    refGfrDialog.value.open();
  };

  watch(
    () => laborForm.gfr.gfr_algorithm,
    () => {
      calculateGfr();
    },
  );

  watch(
    () => laborForm.weight.value,
    () => {
      laborForm.weight.mc_unit_id = laborForm.weight.core.unitList[0].id;
      laborForm.weight.unit = laborForm.weight.core.unitList[0].unit;

      if (!laborForm.weight.value) {
        return;
      }

      if (laborForm.weight.value >= 400) {
        laborForm.errors.weight = "Bitte ein sinnvolles Gewicht eingeben.";
        return;
      } else {
        laborForm.errors.weight = null;
      }

      calculateBmi();
      calculateGfr();
    },
  );

  watch(
    () => laborForm.creatinine,
    () => {
      if (!laborForm.creatinine.unit) {
        laborForm.creatinine.mc_unit_id = laborForm.creatinine.core.unitList[0].id;
        laborForm.creatinine.unit = laborForm.creatinine.core.unitList[0].unit;
      }

      if (creatinineReferenceValue.value && laborForm.creatinine.value) {
        laborForm.creatinine.deviation = getDeviationFromValue(
          laborForm.creatinine.value,
          creatinineReferenceValue.value.low,
          creatinineReferenceValue.value.high,
        );
      }

      calculateGfr();
    },
    {deep: true},
  );

  const handleCreatinineUnitChange = (newCreatinineUnit) => {
    laborForm.creatinine.value = null;
    laborForm.creatinine.deviation = "normal";

    laborForm.creatinine.mc_unit_id = newCreatinineUnit.id;
    laborForm.creatinine.unit = newCreatinineUnit.unit;
  };

  const calculateBmi = () => {
    if (laborForm.weight.value === null || laborForm.size.value === null) {
      return;
    }

    const weightAsNumber = NumberHelper.formatStringAsFloat(laborForm.weight.value);
    const sizeAsNumber = NumberHelper.formatStringAsFloat(laborForm.size.value);

    laborForm.bmi.value = Math.round(weightAsNumber / (sizeAsNumber * sizeAsNumber)).toString();
    laborForm.bmi.mc_unit_id = laborForm.bmi.core.unitList[0].id;
    laborForm.bmi.unit = laborForm.bmi.core.unitList[0].unit;

    laborForm.bmi.deviation = getDeviationFromValue(
      NumberHelper.formatStringAsFloat(laborForm.bmi.value),
      bmiReferenceValue.value.low,
      bmiReferenceValue.value.high,
    );
  };

  const calculateGfr = () => {
    laborForm.errors.gfr = null;
    state.gfrWeightOrBmiError = false;

    laborForm.gfr.value = ""; // must be string!!
    laborForm.gfr.type = laborForm.gfr.core.type;
    laborForm.gfr.mc_laborvalue_id = laborForm.gfr.core.mc_laborvalue_id;
    laborForm.gfr.mc_laborvalue_key = laborForm.gfr.core.mc_laborvalue_key;
    laborForm.gfr.mc_unit_id = null; // will be set below; depends on algorithm
    laborForm.gfr.unit = null; // will be set below; depends on algorithm
    laborForm.gfr.deviation = "normal"; // default is always "normal"

    if (!laborForm.gfr.gfr_algorithm || laborForm.gfr.gfr_algorithm === "") {
      return;
    } else if (
      laborForm.gfr.gfr_algorithm === "useCockcroftGault" &&
      (laborForm.weight.value > 120 || laborForm.bmi.value > 30)
    ) {
      state.gfrWeightOrBmiError = true;
      return;
    }

    // get specific unit depending on algorithm
    let unit =
      laborForm.gfr.gfr_algorithm === "useCockcroftGault"
        ? laborForm.gfr.core.unitList.find((item) => item.unit === "ml/min")
        : laborForm.gfr.core.unitList.find((item) => item.unit === "ml/min/1,73 m²");
    // fallback
    if (!unit) {
      unit = laborForm.gfr.core.unitList[0];
    }

    laborForm.gfr.mc_unit_id = unit.id;
    laborForm.gfr.unit = unit.unit;

    privacy.decryptPatient(page.props.patient).then((decryptedPatient) => {
      const now = new Date();
      const birthdate = new Date(decryptedPatient.birthdate);
      let age = now.getFullYear() - birthdate.getFullYear();
      const m = now.getMonth() - birthdate.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < birthdate.getDate())) {
        age--;
      }

      let creatinine = NumberHelper.formatStringAsFloat(laborForm.creatinine.value);

      if (laborForm.creatinine.mc_unit_id === 14) {
        creatinine = creatinine * 0.0113;
      }

      const gfrCalculator = new Gfr();
      const newGfrValue = gfrCalculator
        .setGender(state.gender)
        .setAge(age)
        .setWeight(NumberHelper.formatStringAsFloat(laborForm.weight.value))
        .setCreatinine(creatinine)
        .getGfr(laborForm.gfr.gfr_algorithm);

      if (!newGfrValue) {
        laborForm.errors.gfr = "Fehlende Werte für die Berechnung.";
        return null;
      }

      laborForm.gfr.value = newGfrValue.toString();

      if (gfrReferenceValue.value && newGfrValue) {
        laborForm.gfr.deviation = getDeviationFromValue(
          newGfrValue,
          gfrReferenceValue.value.low,
          gfrReferenceValue.value.high,
        );
      }
    });
  };

  const saveGfr = async () => {
    laborForm.put(
      route("laborvalues.mass-update-or-create", {
        patient: page.props.patient.id,
        record: page.props.record.id,
      }),
      {
        preserveScroll: true,
        preserveState: true,
        only: ["flash", "errors", "record", "laborvalues", "diseases"],
        onBefore: () => {
          setIsMassUpdateProcessing("laborValue", true);
          isLoading.value = true;
        },
        onFinish: () => {
          isLoading.value = false;
          setIsMassUpdateProcessing("laborValue", false);
          closeDialog();
        },
      },
    );
  };

  const closeDialog = () => {
    refGfrDialog.value.close();
  };

  defineExpose({
    open,
  });
</script>

<template>
  <ComponentDialog
    ref="refGfrDialog"
    dialog-title="(e)GFR berechnen"
    dialog-width="medium"
    :is-loading="isLoading"
    :test-id="getTestId('gfr-calculator')"
  >
    <template #content>
      <div class="flex flex-col gap-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <div class="flex items-end space-x-1">
              <label class="text-gray-500" for="gfr-algorithm">Algorithmus</label>

              <MedInfoButton
                size="lg"
                side="right"
                align="start"
                aria-label="Algorithmus Info"
                disable-portal
                :data-test="getTestId('gfr-calculator-algorithm-info-button')"
              >
                <div class="flex flex-col gap-3 text-sm">
                  <template
                    v-if="
                      laborForm.gfr.gfr_algorithm === 'useCkdEpi' ||
                      laborForm.gfr.gfr_algorithm === 'useCkdEpiBlackEthnic'
                    "
                  >
                    <p>
                      Die CKD-EPI-Formel schätzt die GFR im Grenzbereich einer beginnenden Niereninsuffizienz genauer
                      als die MDRD-Formel. Berechnung erfolgt anhand idealisierter KOF.
                    </p>

                    <p>
                      CAVE: Die Formel besitzt eine eingeschränkte Aussagekraft bei Kindern und Jugendlichen, sehr hohem
                      Alter, starkem Über- oder Untergewicht, extremer Muskelmasse, Skelettmuskelerkrankungen.
                    </p>

                    <p>
                      <strong class="font-semibold">Berechnung:</strong>
                      <br />
                      <i>
                        GFR = 142 × (SKr[mg/dl] / A)
                        <sup>B</sup>
                        × 0,9938
                        <sup>Alter[Jahre]</sup>
                        × 1,012 (wenn weiblich)
                      </i>
                    </p>

                    <ul class="ml-2 list-disc space-y-1 pl-2">
                      <li>Geschlechtsabhängiger Faktor A: 0,7 (Frau) bzw. 0,9 (Mann)</li>
                      <li>
                        Geschlechtsabhängiger Faktor B: -0,241 (Frau bei SKr ≤ 0,7 mg/dl); -1,2 (Frau bei SKr > 0,7
                        mg/dl); -0,302 (Mann bei SKr ≤ 0,9 mg/dl); -1,2 (Mann bei SKr > 0,9 mg/dl)
                      </li>
                      <li>weitere Korrekturfaktoren: 1,012 (Frau)</li>
                    </ul>
                  </template>

                  <template v-else-if="laborForm.gfr.gfr_algorithm === 'useMayoClinic'">
                    <p>Gut geeignet wenn eine genaue Diagnose noch nicht bekannt ist.</p>

                    <p>
                      <strong class="font-semibold">Berechnung:</strong>
                      <br />
                      <i>
                        GFR = exp(1,911 + 5,249 / SKr[mg/dl] - 2,114 / SKr[mg/dl]
                        <sup>2</sup>
                        - 0,00686 × Alter[Jahre] - GF)
                      </i>
                    </p>

                    <ul class="ml-2 list-disc space-y-1 pl-2">
                      <li>Geschlechtsfaktor GF: 0,205 (Frau); 0 (Mann)</li>
                    </ul>

                    <p>
                      Wenn der Serumkreatininwert &#060; 0,8 mg/dl beträgt, wird das Serumkreatinin auf 0,8 mg/dl
                      festgelegt. Daher ändert sich die berechnete GFR bei niedrigem Serumkreatinin im Online-Rechner
                      nicht mehr.
                    </p>
                  </template>

                  <template
                    v-else-if="
                      laborForm.gfr.gfr_algorithm === 'useMdrd' || laborForm.gfr.gfr_algorithm === 'useMdrdBlackEthnic'
                    "
                  >
                    <p>
                      CAVE: Die MDRD-Formel gilt nur für Personen im Alter von 18 bis 70 Jahren und ist für verschiedene
                      Personengruppen ungeeignet (Nierentransplantierte, Dialysepatienten, Schwangere,
                      Diabetes-Patienten, die Insulin spritzen, Patienten mit weiteren schweren Krankheiten, Personen
                      mit großer Muskelmasse oder Unterernährung). Wählen Sie in den genannten Fällen eine andere Formel
                      z.B CKD-EPI. Es existieren verschiedene Versionen der Formel, wobei die folgende die
                      gebräuchlichste ist.
                    </p>

                    <p>
                      <strong class="font-semibold">Berechnung:</strong>
                      <br />
                      <i>
                        GFR = 186 × SKr[mg/dl]
                        <sup>-1,154</sup>
                        × Alter[Jahre]
                        <sup>-0,203</sup>
                      </i>
                    </p>

                    <ul class="ml-2 list-disc space-y-1 pl-2">
                      <li>Korrekturfaktoren: 0,742 (Frau), 1,21 (schwarze Hautfarbe)</li>
                    </ul>
                  </template>

                  <template v-else-if="laborForm.gfr.gfr_algorithm === 'useCockcroftGault'">
                    <p>
                      CAVE: Formel wurde aufgrund einer eingeschränkten Fallzahl entwickelt, dies führt zu einer
                      Beeinträchtigung der Validität.
                    </p>

                    <p>
                      <strong class="font-semibold">Berechnung:</strong>
                      <br />
                      <i>GFR = ((140 − Alter[Jahre]) / SKr[mg/dl]) × (Körpergewicht[kg]) / 72)</i>
                    </p>

                    <ul class="ml-2 list-disc space-y-1 pl-2">
                      <li>Korrekturfaktor: 0,85 (Frau)</li>
                    </ul>
                  </template>

                  <template v-else>Keine Berechnung.</template>
                </div>
              </MedInfoButton>
            </div>

            <ComponentSelect
              v-model="laborForm.gfr.gfr_algorithm"
              :options="patientGfralgorithm"
              :test-id="getTestId('gfr-calculator-dialog-algorithm')"
            />
          </div>
        </div>

        <div v-if="laborForm.gfr.gfr_algorithm?.length > 0" class="grid grid-cols-2 gap-4">
          <template v-if="laborForm.gfr.gfr_algorithm === 'useCockcroftGault'">
            <ComponentInput
              v-model="laborForm.weight.value"
              label="Gewicht in kg"
              :helper-text="bmiHelperText"
              :validation="laborForm.errors.weight"
              :test-id="getTestId('gfr-calculator-dialog-weight')"
            />

            <div />
          </template>

          <ComponentInput
            v-model="laborForm.creatinine.value"
            label="Kreatinin"
            :helper-text="creatinineReferenceText"
            :validation="laborForm.errors.creatinine"
            :test-id="getTestId('gfr-calculator-dialog-creatine')"
          />

          <ComponentSelect
            label="Einheit (opt.):"
            :model-value="laborForm.creatinine.mc_unit_id"
            :options="laborForm.creatinine.core.unitList"
            key-value="id"
            key-name="unit"
            return-type="object"
            :test-id="getTestId('gfr-calculator-dialog-creatine-unit')"
            @change="handleCreatinineUnitChange"
          />
        </div>

        <div>
          <h2 class="mb-2 text-base">Ergebnis</h2>

          <ComponentAlert v-if="laborForm.errors.gfr" type="error" :test-id="getTestId('gfr-calculator-dialog-result')">
            <p>{{ laborForm.errors.gfr }}</p>
          </ComponentAlert>

          <ComponentAlert
            v-if="state.gfrWeightOrBmiError"
            type="error"
            :test-id="getTestId('gfr-calculator-dialog-result-bmi-gfr')"
          >
            <p>
              Laut Ihren Patientenangaben weist Ihr(e) Patient(in) einen BMI >30 kg/m² und/oder ein Körpergewicht von
              >120 kg auf. Die von Ihnen gewählte Formel ist zur korrekten Berechnung der Nierenfunktion bei Vorliegen
              dieser Körperkonstitution ungeeignet. Aufgrund dessen ist eine Berechnung der (e)GFR mit der
              <button
                class="cursor-pointer text-mcred hover:underline"
                @click="laborForm.gfr.gfr_algorithm = 'useCkdEpi'"
              >
                CKD-EPI-Formel
              </button>
              empfehlenswert. Mehr Informationen zur korrekten Berechnung der Nierenfunktionsleistung finden Sie
              <MedLink
                variant="primary"
                target="_blank"
                href="https://medicheck.pharma4u.de/url-redirect/gfr-calculation"
                :data-test="getTestId('gfr-calculator-info-link')"
              >
                hier
              </MedLink>
              .
            </p>
          </ComponentAlert>

          <ComponentAlert
            v-if="state.gender === 'diverse'"
            type="warning"
            :test-id="getTestId('gfr-calculator-dialog-result-gender')"
          >
            <p>
              Die Berechnung ist mit dem für den Patienten hinterlegten Geschlecht "divers" nicht möglich, es wird daher
              das Geschlecht "weiblich" verwendet.
            </p>
          </ComponentAlert>

          <div class="grid grid-cols-2 gap-4 rounded bg-gray-100 p-2">
            <ComponentFakeInput
              :value="gfrValue"
              :label="
                '(e)GFR in ' + (laborForm.gfr.gfr_algorithm === 'useCockcroftGault' ? 'ml/min' : 'ml/min/1,73 m²')
              "
              :helper-text="gfrReferenceText"
              :test-id="getTestId('gfr-calculator-dialog-result-gfr-value')"
            />

            <ComponentFakeInput
              class="mt-6"
              :value="laborbardeviationValues[laborForm.gfr.deviation] ?? ''"
              :test-id="getTestId('gfr-calculator-dialog-result-gfr-deviation')"
            />
          </div>
        </div>
      </div>
    </template>

    <template #actions>
      <MedButton
        variant="primary"
        :disabled="hasError"
        :data-test="getTestId('gfr-calculator-dialog-save-button')"
        @click="saveGfr"
      >
        Übernehmen & Speichern
      </MedButton>

      <MedButton :data-test="getTestId('gfr-calculator-dialog-close-button')" @click="closeDialog">Abbrechen</MedButton>
    </template>
  </ComponentDialog>
</template>
